<template>
  <div
    class="category-product-card"
    :class="{ 'without-top-radius': product.status_akeneo === 2182 }"
  >
    <div v-if="!isAuthenticated" class="not-logged-in">
      <NuxtLink :to="localePath(getProductPath(product))" :draggable="false">
        <div
          class="img-wrapper"
          @mouseenter="mouseOverCard = true"
          @mouseleave="mouseOverCard = false"
        >
          <nuxt-img
            quality="90"
            format="webp"
            placeholder
            :src="productImage"
            :alt="product.thumbnail.label"
          />
        </div>
      </NuxtLink>
      <p class="brand font-fgb" v-if="brandsStore.getBrandById(product.brand)">
        {{ brandsStore.getBrandById(product.brand).label }}
      </p>
      <p class="brand" v-else>Shots</p>
      <NuxtLink :to="localePath(getProductPath(product))" :draggable="false">
        <p class="product-title">{{ product.name }}</p>
      </NuxtLink>
      <p class="sku">{{ product.sku }}</p>
      <div class="price">
        <span class="price-text font-fgb">{{ $t("Retail price") }}</span>
        <span class="price-number font-fgb">{{
          $fc(productGetters.getPrice(product).regular)
        }}</span>
      </div>
      <div class="buttons">
        <button class="btn" @click="$router.push(localePath('/register'))">
          {{ $t("Sign up") }}
        </button>
        <button class="btn" @click="$router.push(localePath('/login'))">
          {{ $t("Login") }}
        </button>
      </div>
    </div>
    <div v-else class="logged-in">
      <NuxtLink :to="localePath(getProductPath(product))" :draggable="false">
        <div
          class="img-wrapper"
          @mouseenter="mouseOverCard = true"
          @mouseleave="mouseOverCard = false"
        >
          <nuxt-img
            quality="90"
            format="webp"
            placeholder
            :src="productImage"
            :alt="product.thumbnail.label"
          />
        </div>
      </NuxtLink>
      <p class="brand font-fgb" v-if="brandsStore.getBrandById(product.brand)">
        {{ brandsStore.getBrandById(product.brand).label }}
      </p>
      <p class="brand font-fgb" v-else>Shots</p>
      <NuxtLink :to="localePath(getProductPath(product))" :draggable="false">
        <p class="product-title">{{ product.name }}</p>
      </NuxtLink>
      <p class="sku">{{ product.sku }}</p>
      <p class="barcode">Barcode: {{ product.barcode }}</p>
      <div class="stock">
        <span v-show="parseInt(product.shots_stock) > 0" class="in-stock traffic-green"
          >{{ product.shots_stock }} {{ $t("in stock") }}</span
        >
        <span v-show="!parseInt(product.shots_stock)" class="in-stock traffic-red">{{
          $t("Out of stock")
        }}</span>
        <div
          class="in-production"
          v-show="product.expected_stock && product.expected_stock > 0"
        >
          <span class="traffic-yellow"
            >{{ product.expected_stock }} {{ $t("in production") }}</span
          >
          <span class="font-fgb" v-if="product.expected_stock_date"
            >{{ $t("Est. arrival") }}
            {{ new Date(product.expected_stock_date).toLocaleDateString() }}</span
          >
        </div>
      </div>
      <div class="price">
        <div class="retail">
          <p>
            {{ $t("Retail price") }}
            <span class="vat-notice">(incl. {{ vatText }} {{ $t("VAT") }})</span>
          </p>
          <p>{{ $fc(productGetters.getPrice(product).regular) }}</p>
        </div>
        <div class="wholesale">
          <p>
            {{ $t("Wholesale price") }}
            <span class="vat-notice">(excl. {{ vatText }} {{ $t("VAT") }})</span>
          </p>
          <p v-if="yourPrice > 0">{{ $fc(wholesalePrice) }}</p>
          <div v-else class="lds-spinner">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
      <div class="specific-customer">
        <div class="margin">
          <p>{{ $t("Your discount") }}</p>
          <p>{{ yourDiscount }}%</p>
        </div>
        <div class="margin">
          <p>{{ $t("Your margin") }}</p>
          <p v-if="productGetters.getPrice(product).regular === 0">-</p>
          <p v-else-if="yourPrice > 0">{{ priceMargin }}%</p>
          <div v-else>
            <div class="lds-spinner">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
        <div class="customer-price">
          <p>
            {{ $t("Your price") }}
            <span class="vat-notice">(excl. {{ vatText }} {{ $t("VAT") }})</span>
          </p>
          <p v-if="yourPrice > 0">{{ $fc(yourPrice) }}</p>
          <div v-else>
            <div class="lds-spinner">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </div>
      <div class="quantity">
        <p>{{ $t("Quantity") }}</p>
        <input type="number" v-model="qty" />
        <div class="case-count" v-if="product.master_case_qty">
          {{ $t("Case count:") }} {{ product.master_case_qty }}
        </div>
      </div>

      <div
        class="add-to-cart"
        @click="addProductToCart(product, qty)"
        v-if="!(product.exclude_for && isDropshipper) && !loveSenseRule"
      >
        <Icon class="wishIcon" icon="material-symbols:shopping-cart-outline-rounded" />
      </div>
      <div class="add-to-wishlist" @click="$emit('add-to-wishlist', product)">
        <Icon class="wishIcon" :icon="isInWishlist ? 'solar:heart-bold' : 'ph:heart'" />
      </div>
    </div>
    <div class="discontinued" v-if="product.status_akeneo === 2182">
      {{ $t("Discontinued Soon") }}
    </div>
    <div class="outlet" v-if="product.outlet === 1">
      {{ $t("Outlet") }}
    </div>
    <div
      class="product-overlay"
      v-if="(product.exclude_for && isDropshipper) || loveSenseRule"
    >
      <div class="overlay-message">
        {{ $t("Product not available") }}
      </div>
    </div>
  </div>
</template>

<script>
import { useUser } from "~/modules/customer/composables/useUser";
import productGetters from "~/modules/catalog/product/getters/productGetters";
import { useProduct } from "~/composables";
import useWishlist from "~/modules/wishlist/composables/useWishlist";
import { Icon } from "@iconify/vue2";
import { ref, computed, useContext } from "@nuxtjs/composition-api";
import { useBrandsStore } from "~/stores/brands";
import { getPrice as getProductPrice } from "~/modules/catalog/product/getters/productGetters";

export default {
  props: {
    product: {
      type: Object,
      required: true,
      default: () => {},
    },
    stockData: {
      type: Object,
      default: () => {},
    },
    priceData: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    Icon,
  },
  setup(props, { emit }) {
    const { isAuthenticated, userAdv } = useUser();
    const { getProductPath } = useProduct();
    const { isInWishlist } = useWishlist();
    const qty = ref(1);
    const brandsStore = useBrandsStore();
    const {
      app: { i18n, $cookies },
    } = useContext();
    const productPrice = computed(() => getProductPrice(props.product).regular);

    const isDropshipper = computed(() => userAdv?.value?.is_dropshipper);
    const customerGroupId = computed(() => userAdv?.value?.custom_group_id);

    const isForbiddenCountry = computed(() => {
      const forbiddenCountries = ["RU", "GB", "UA", "TR"];
      const userCountry = $cookies.get("ip-country")?.split(".")[0];
      if (userCountry) {
        return forbiddenCountries.includes(userCountry);
      } else {
        return false;
      }
    });

    const loveSenseRule = computed(() => {
      return (
        (customerGroupId.value === 5 &&
          brandsStore.getBrandById(props.product.brand) &&
          brandsStore.getBrandById(props.product.brand).label === "Lovense") ||
        (isForbiddenCountry.value &&
          brandsStore.getBrandById(props.product.brand) &&
          brandsStore.getBrandById(props.product.brand).label === "Lovense")
      );
    });

    const amountInStock = computed(() => props.stockData?.availableInventory);
    const amountInProduction = computed(() => {
      if (props.stockData && props.stockData.nextExpectedReceiptAvailable) {
        return props.stockData.nextExpectedReceipts[0];
      } else {
        return false;
      }
    });

    const mouseOverCard = ref(false);

    const productImage = computed(() => {
      if (
        !mouseOverCard.value ||
        (props.product.media_gallery && props.product.media_gallery.length < 1)
      ) {
        return props.product.thumbnail.url;
      } else if (props.product.media_gallery && props.product.media_gallery.length > 1) {
        return props.product.media_gallery[1].url;
      } else {
        return props.product.thumbnail.url;
      }
    });

    const showAmountInProd = computed(() => {
      if (props.stockData && props.stockData.nextExpectedReceiptAvailable) {
        return true;
      } else {
        return false;
      }
    });

    const wholesalePrice = computed(() => {
      if (props.priceData) {
        return props.priceData.wholesalePrice;
      } else {
        return 0;
      }
    });

    const yourPrice = computed(() => {
      if (props.priceData) {
        return props.priceData.unitPrice;
      } else {
        return 0;
      }
    });

    const vatText = computed(() => {
      if (props.product.fe_tax === "High") {
        return "21%";
      } else if (props.product.fe_tax === "Low") {
        return "9%";
      } else {
        return "0%";
      }
    });

    const productTaxMargin = computed(() => {
      if (props.product.fe_tax === "High") {
        return 1.21;
      } else if (props.product.fe_tax === "Low") {
        return 1.09;
      } else {
        return 1;
      }
    });

    const priceMargin = computed(() => {
      if (props.priceData && productTaxMargin.value) {
        return (
          ((productPrice.value / productTaxMargin.value - props.priceData.unitPrice) /
            (productPrice.value / productTaxMargin.value)) *
          100
        ).toFixed();
      } else {
        return 0;
      }
    });

    const yourDiscount = computed(() => {
      if (props.priceData) {
        return (
          ((wholesalePrice.value - yourPrice.value) / wholesalePrice.value) *
          100
        ).toFixed();
      } else {
        return 0;
      }
    });

    const addProductToCart = (product, qty) => {
      if (product.exclude_for && isDropshipper.value) return;
      emit("add-to-cart", { product, quantity: qty });
    };

    return {
      isAuthenticated,
      productGetters,
      getProductPath,
      qty,
      isInWishlist: computed(() => isInWishlist({ product: props.product })),
      brandsStore,
      amountInStock,
      amountInProduction,
      showAmountInProd,
      addProductToCart,
      wholesalePrice,
      priceMargin,
      productImage,
      mouseOverCard,
      vatText,
      yourPrice,
      isDropshipper,
      yourDiscount,
      isForbiddenCountry,
      customerGroupId,
      loveSenseRule,
    };
  },
};
</script>

<style lang="scss" scoped>
.category-product-card {
  @apply border border-solid rounded-3xl border-grey p-4 font-suwa relative;
  .not-logged-in {
    height: 430px;
    width: 250px;
    @media only screen and (max-width: 1530px) {
      height: auto;
      margin: 0 auto;
    }
    @media only screen and (max-width: 1200px) {
      width: auto;
    }
    .img-wrapper {
      @apply mb-4;
      max-width: 100%;
      width: auto;
      height: 200px;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .brand {
      @apply text-base text-brownish-grey mb-2;
      white-space: nowrap;
      overflow: hidden;
    }

    .product-title {
      @apply font-mont font-bold mb-1;
      width: 100%;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      height: 45px;
    }

    .sku {
      @apply text-base font-fgb text-brownish-grey mb-2;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .price {
      @apply flex justify-between font-bold mb-2;
      @media only screen and (max-width: 768px) {
        @apply flex-col;
      }
    }

    .buttons {
      @apply flex justify-between pt-4 gap-2;
      border-top: 2px solid #e8e8e8;
      .btn {
        @apply px-8 px-4;
        min-width: 120px;
        @media only screen and (max-width: 1200px) {
          min-width: 110px;
        }
      }
    }
  }

  .logged-in {
    @apply relative;
    height: 550px;
    width: 250px;
    margin: auto;
    @media only screen and (max-width: 1530px) {
      height: auto;
      margin: 0 auto;
    }
    @media only screen and (max-width: 1200px) {
      width: auto;
    }
    .img-wrapper {
      @apply mb-4;
      max-width: 100%;
      width: auto;
      height: 200px;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: opacity 0.3s ease;
      }
    }

    .brand {
      @apply text-base text-brownish-grey mb-2;
      white-space: nowrap;
      overflow: hidden;
    }

    .product-title {
      @apply font-mont font-bold mb-1;
      width: 100%;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      height: 45px;
    }

    .sku {
      @apply text-base font-fgb text-brownish-grey;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .barcode {
      @apply text-brownish-grey mb-3;
      font: normal normal 500 10px/13px Montserrat;
    }

    .stock {
      @apply flex justify-between text-xs mb-2;
      .in-production {
        @apply flex flex-col;
        span {
          @apply mb-1 font-fgb;
        }
      }
    }

    .traffic-green {
      @apply text-green relative pl-4 font-fgb;
      &::before {
        @apply bg-green absolute;
        left: 0;
        top: 2px;
        content: "";
        display: inline-block;
        width: 12px;
        height: 12px;
        -moz-border-radius: 8.5px;
        -webkit-border-radius: 8.5px;
        border-radius: 8.5px;
      }
    }
    .traffic-yellow {
      @apply text-orange relative pl-4 font-fgb;
      &::before {
        @apply bg-orange absolute;
        left: 0;
        top: 2px;
        content: "";
        display: inline-block;
        width: 12px;
        height: 12px;
        -moz-border-radius: 8.5px;
        -webkit-border-radius: 8.5px;
        border-radius: 8.5px;
      }
    }
    .traffic-red {
      @apply text-warm-red relative pl-4 font-fgb;
      &::before {
        @apply bg-warm-red absolute;
        left: 0;
        top: 2px;
        content: "";
        display: inline-block;
        width: 12px;
        height: 12px;
        -moz-border-radius: 8.5px;
        -webkit-border-radius: 8.5px;
        border-radius: 8.5px;
      }
    }

    .price {
      @apply text-xs font-fgb;
      .retail,
      .wholesale {
        @apply flex justify-between mb-1;
      }
      @media only screen and (max-width: 768px) {
        @apply flex-col;
      }
    }

    .vat-notice {
      color: #8a8a8a;
    }

    .specific-customer {
      @apply text-xs my-2 pt-2 font-fgb;
      border-top: 2px solid #eeeeee;
      .margin,
      .customer-price {
        @apply flex justify-between mb-1;
      }
      .customer-price {
        @apply font-bold;
      }
    }
    .quantity {
      @apply flex justify-start gap-2 items-center mt-4 font-fgb;
      p {
        @apply text-base text-brownish-grey;
      }
      input {
        @apply font-bold text-base font-mont;
        border: 1px solid #e8e8e8;
        width: 50px;
        border-radius: 20px;
        padding: 5px;
        text-align: center;
      }

      /* Chrome, Safari, Edge, Opera */
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Firefox */
      input[type="number"] {
        -moz-appearance: textfield;
      }

      .case-count {
        @apply text-brownish-grey text-xs ml-2;
      }
    }

    .add-to-cart {
      @apply cursor-pointer;
      position: absolute;
      bottom: -37px;
      right: -36px;
      background: rgb(227, 6, 19);
      background: linear-gradient(90deg, rgba(227, 6, 19, 1) 0%, rgba(167, 3, 3, 1) 68%);
      padding: 15px;
      border-radius: 50%;

      .iconify {
        @apply text-white text-3xl;
      }

      @media only screen and (max-width: 1023px) {
        bottom: -25px;
        right: -20px;
      }
      @media only screen and (max-width: 769px) {
        right: -16px;
      }
    }

    .add-to-wishlist {
      @apply absolute cursor-pointer;
      top: 0;
      right: 0;
      .iconify {
        @apply text-2xl;
      }
    }
  }
  @media only screen and (max-width: 1280px) {
    height: auto;
    width: 100%;
    min-width: 100%;
  }
  @media only screen and (max-width: 480px) {
    width: 100%;
  }

  .discontinued {
    @apply bg-fire-red text-white font-mont text-sm font-bold;
    position: absolute;
    top: -20px;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    width: 100%;
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }

  .outlet {
    @apply text-white font-mont text-sm font-bold;
    position: absolute;
    top: 23px;
    left: 0;
    background: rgb(227, 6, 19);
    background: linear-gradient(90deg, rgba(227, 6, 19, 1) 0%, rgba(167, 3, 3, 1) 68%);
    border-bottom-right-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
    padding: 5px 15px;
  }

  &.without-top-radius {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    @media only screen and (max-width: 1280px) {
      margin-top: 1.2rem;
    }
  }

  .product-overlay {
    @apply absolute rounded-3xl flex justify-center;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.85);
    .overlay-message {
      @apply px-4 py-6 text-center max-w-[194px] mx-auto text-white rounded-2xl uppercase font-mont font-bold;
      background: rgb(227, 6, 19);
      background: linear-gradient(90deg, rgba(227, 6, 19, 1) 0%, rgba(167, 3, 3, 1) 68%);
      margin: 0;
      position: absolute;
      top: 50%;
      -ms-transform: translateY(-50%);
      transform: translateY(-50%);
      font: normal normal bold 18px/18px;
    }
  }
}
</style>
